// Supporting content for the premium category/expertise pages.
// Editorial copy only — no certifications, client names or figures beyond what
// the existing category data already states.

export const CATEGORY_EXTRAS = {
  fasteners: {
    hubs: [
      ["Ludhiana, India", "Our home ground for fasteners and hardware, with a partner plant working to IATF 16949."],
      ["Wenzhou & Handan, China", "High-volume commodity fasteners and standard parts at scale."],
      ["Shanghai / Ningbo corridor", "Export consolidation, coatings and third-party testing support."],
    ],
    considerations: [
      "Send the drawing with grade, coating and standard — pricing changes sharply between 8.8, 10.9 and 12.9.",
      "Coating choice (zinc, ZnNi, HDG, geomet) drives both cost and salt-spray performance.",
      "Commodity sizes are priced per kg; drawing-based specials are priced per piece with tooling.",
      "Duty and freight can outweigh a lower unit price — compare landed cost, not ex-works cost.",
    ],
    verification: [
      "Material certificates and heat-number traceability per lot.",
      "PPAP documentation where the application requires it.",
      "Salt-spray, torque and tensile test reports before shipment.",
      "Factory-versus-trader check so you know who actually makes the part.",
    ],
    guides: [["fastener-buyers-manual", "The Fastener Buyer's Manual"], ["india-sourcing-atlas", "India Sourcing Atlas"], ["verification-toolkit", "Supplier Verification Toolkit"]],
  },
  "cycle-parts": {
    hubs: [
      ["Ludhiana, India", "India's cycle capital — frames, chains, hubs and components bought here every quarter."],
      ["Tianjin & Kunshan, China", "Full component ecosystem including e-bike drivetrains and controllers."],
      ["Ho Chi Minh City, Vietnam", "Assembly capacity for buyers building a China+1 position."],
    ],
    considerations: [
      "Decide early whether you are buying components, CKD kits or complete bicycles — the supply base differs.",
      "E-bike drivetrains and batteries carry their own compliance and shipping restrictions.",
      "Consolidating several component vendors into one shipment usually beats separate bookings.",
      "Finish quality (plating, paint, decals) is the most common source of rejection on cycle parts.",
    ],
    verification: [
      "Supplier audit before the first order.",
      "Dimensional and finish checks against your approved sample.",
      "Load and fatigue test coordination for structural parts.",
      "Pre-shipment AQL inspection across every consolidated vendor.",
    ],
    guides: [["india-sourcing-atlas", "India Sourcing Atlas"], ["china-sourcing-atlas", "The China Sourcing Atlas"], ["first-container-playbook", "First Container Playbook"]],
  },
  textiles: {
    hubs: [
      ["Ludhiana & Tiruppur, India", "Hosiery, knitwear and cotton-led programmes with strong mid-volume flexibility."],
      ["Surat, India", "Synthetic fabrics, trims and accessory supply."],
      ["Shaoxing & Keqiao, China", "Fabric mills, dyeing capacity and accessory clusters at scale."],
    ],
    considerations: [
      "Lock the fabric composition, GSM and shade before price — everything else moves with them.",
      "Lab dips and sealed samples prevent the most expensive argument later: colour.",
      "Trims and accessories often come from different vendors than the garment itself.",
      "Seasonal capacity is real: book knitting and dyeing slots ahead of your peak.",
    ],
    verification: [
      "Fabric and shade approval against sealed samples.",
      "In-line and pre-shipment AQL inspection with measurement checks.",
      "Testing coordination for composition, shrinkage and colour fastness.",
      "Confirmation of in-house versus sub-contracted stitching capacity.",
    ],
    guides: [["india-sourcing-atlas", "India Sourcing Atlas"], ["private-label-blueprint", "Private Label Blueprint"], ["supplier-tricks", "Supplier Tricks & Costly Mistakes"]],
  },
  electronics: {
    hubs: [
      ["Shenzhen, China", "Components, PCBA and fast iteration for consumer electronics."],
      ["Guangzhou & Dongguan, China", "Assembly, enclosures, cables and accessory manufacturing."],
      ["Ho Chi Minh City, Vietnam", "Assembly alternatives for tariff-sensitive programmes."],
    ],
    considerations: [
      "Separate the BOM from the assembly quote — that is where hidden substitutions live.",
      "Insist on a golden sample; component swaps are the standard cost-down trick.",
      "Certification (CE, FCC, BIS) belongs in the specification, not the afterthought stage.",
      "Batteries and adaptors change your freight options and paperwork completely.",
    ],
    verification: [
      "Functional testing protocol agreed before production.",
      "Component and BOM verification against the approved sample.",
      "Pre-shipment AQL inspection with power-on and safety checks.",
      "Factory-versus-trader check and export-experience review.",
    ],
    guides: [["china-sourcing-atlas", "The China Sourcing Atlas"], ["china-plus-one-atlas", "China+1 Atlas"], ["verification-toolkit", "Supplier Verification Toolkit"]],
  },
  yiwu: {
    hubs: [
      ["Yiwu, China", "The world's largest small-commodity market — district by district, trade by trade."],
      ["Ningbo, China", "Consolidation, inspection and container loading close to the port."],
      ["Guangzhou, China", "Alternative sourcing for packaging, print and gift lines."],
    ],
    considerations: [
      "Yiwu rewards a clear shopping list: item, target price, quantity and packaging.",
      "Most booths are traders — the factory behind the booth is what matters for repeat orders.",
      "Mixed-container buying needs one accountable party for QC and loading.",
      "Packaging and barcoding decisions are cheapest to make before production, not after.",
    ],
    verification: [
      "Sample approval per SKU before consolidation.",
      "Carton, barcode and packaging checks against your retail requirement.",
      "Consolidated pre-shipment inspection across all vendors in the container.",
      "Loading supervision and photographic record.",
    ],
    guides: [["yiwu-handbook", "The Yiwu Operator's Handbook"], ["first-container-playbook", "First Container Playbook"], ["canton-fair-field-manual", "Canton Fair Field Manual"]],
  },
  furniture: {
    hubs: [
      ["Foshan & Shunde, China", "The largest furniture cluster in Asia, from residential to hospitality."],
      ["Ho Chi Minh City & Binh Duong, Vietnam", "Solid-wood and upholstered production for tariff-sensitive markets."],
      ["Jodhpur & Jaipur, India", "Hand-finished, solid wood and character furniture."],
    ],
    considerations: [
      "Material substitution (board grade, foam density, veneer) is the main cost-down risk.",
      "Furniture ships by volume — cartonisation and knock-down design protect your margin.",
      "Hospitality and contract projects need documented fire and durability compliance.",
      "Finish and colour must be approved on a physical sealed sample, never on a screen.",
    ],
    verification: [
      "Material and hardware verification against the approved specification.",
      "Assembly, stability and finish checks before packing.",
      "Carton drop-test and packaging review for container transit.",
      "Loading supervision to protect against transit damage claims.",
    ],
    guides: [["china-plus-one-atlas", "China+1 Atlas"], ["china-sourcing-atlas", "The China Sourcing Atlas"], ["incoterms-landed-cost", "Incoterms & Landed Cost"]],
  },
  medical: {
    hubs: [
      ["Shenzhen & Jiangsu, China", "Devices, consumables and hospital equipment manufacturing."],
      ["New Delhi & Gujarat, India", "Consumables, disposables and selected equipment."],
      ["Bangkok, Thailand", "Selected specialist equipment and regional distribution."],
    ],
    considerations: [
      "Regulatory classification decides everything — confirm it before you shortlist suppliers.",
      "Certification and import licensing remain the buyer's compliance responsibility.",
      "Documentation packs (IFU, labelling, technical file) must be agreed in writing up front.",
      "Sterile and single-use products carry lot-traceability and shelf-life obligations.",
    ],
    verification: [
      "Manufacturing licence and export-history checks.",
      "Quality-system and documentation review against your requirement.",
      "Sample approval and pre-shipment inspection with labelling verification.",
      "Lot traceability and shelf-life confirmation for consumables.",
    ],
    guides: [["verification-toolkit", "Supplier Verification Toolkit"], ["china-sourcing-atlas", "The China Sourcing Atlas"], ["scam-checklist", "Sourcing Scam Checklist"]],
  },
  other: {
    hubs: [
      ["China", "Engineering machinery, modular structures, industrial equipment and consumer ranges."],
      ["India", "Engineering, forgings, hosiery machinery and industrial components."],
      ["Vietnam & Thailand", "China+1 capacity for selected industrial and consumer categories."],
    ],
    considerations: [
      "Tell us the application, not just the product — it changes which market fits best.",
      "Capital equipment needs installation, commissioning and spares agreed in the quotation.",
      "Project and contract buying benefits from a written scope before any deposit.",
      "If we are not the right partner for it, we will say so and point you somewhere sensible.",
    ],
    verification: [
      "Business licence, factory-versus-trader and bank-account matching.",
      "Manufacturing capability and export-experience review.",
      "Scope, specification and acceptance criteria agreed in writing.",
      "Inspection before dispatch, with documentation coordinated end to end.",
    ],
    guides: [["china-sourcing-atlas", "The China Sourcing Atlas"], ["india-sourcing-atlas", "India Sourcing Atlas"], ["incoterms-landed-cost", "Incoterms & Landed Cost"]],
  },
};

export const CATEGORY_SERVICES = [
  ["sourcing", "Supplier discovery & sourcing", "The right cluster, a qualified shortlist and terms that make sense."],
  ["verification", "Supplier verification", "Licence, factory-versus-trader and bank-match checks before you commit."],
  ["inspection", "Quality & inspection", "Clear specifications, approved samples and pre-shipment AQL inspection."],
];

export const TIER_LABEL = {
  1: ["Deep expertise", "Our strongest category. We read the drawings and argue the details with engineers."],
  2: ["Strong market knowledge", "We buy in this category regularly and know the clusters, prices and pitfalls."],
  3: ["Vetted specialist network", "Delivered through vetted specialist partners under our verification standard."],
};
